import {
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { ApiErrorResponse } from '../ApiClient';
import { composeQueryKey } from './composeQueryKey';

export type UseApiQueryOptions<
  ResponseData,
  Key extends QueryKey = QueryKey,
  ResponseError extends Error = ApiErrorResponse,
> = Omit<
  UseQueryOptions<ResponseData, ResponseError, ResponseData, Key>,
  'queryFn'
>;

export type ApiQueryHook<ResponseData, ApiError> = UseQueryResult<
  ResponseData,
  ApiError
> & {
  setData: (data: ResponseData) => ResponseData | undefined;
  invalidate: () => Promise<void>;
  remove: () => void;
};

export const createApiQueryHook =
  <
    QKey extends QueryKey,
    QFunction extends (...args: any[]) => any,
    QArgs extends Parameters<QFunction>,
    ResponseData extends Awaited<ReturnType<QFunction>>,
    QOptions extends Partial<UseApiQueryOptions<ResponseData, QKey | QueryKey>>,
  >(
    baseQueryKey: QKey,
    queryFn: QFunction,
    baseOptions?: Partial<
      UseApiQueryOptions<Awaited<ReturnType<QFunction>>, QKey>
    >,
  ) =>
  (
    ...args: QArgs extends []
      ? [QOptions] | [undefined] | []
      : [...QArgs, QOptions] | QArgs
  ): ApiQueryHook<ResponseData, ApiErrorResponse> => {
    const customOptions: QOptions =
      args.length > queryFn.length ? args.pop() : undefined;

    const queryClient = useQueryClient();
    const previousQueryKeyRef = useRef<
      QKey | QueryKey | QueryKey[] | undefined
    >(undefined);
    const queryKey =
      customOptions?.queryKey ||
      composeQueryKey(baseOptions?.queryKey || baseQueryKey, ...args);

    if (
      previousQueryKeyRef.current &&
      JSON.stringify(previousQueryKeyRef.current) !== JSON.stringify(queryKey)
    ) {
      void queryClient.cancelQueries({
        queryKey: previousQueryKeyRef.current,
      });
    }
    previousQueryKeyRef.current = queryKey;

    const setData = useCallback(
      (data: ResponseData) =>
        queryClient.getQueryData<ResponseData>(queryKey) === data
          ? data
          : queryClient.setQueryData<ResponseData>(queryKey, data),
      [queryClient, queryKey],
    );

    const invalidate = useCallback(
      () => queryClient.invalidateQueries({ queryKey }),
      [queryClient, queryKey],
    );

    const remove = useCallback(
      () => queryClient.removeQueries({ queryKey }),
      [queryClient, queryKey],
    );

    const query = useQuery<
      ResponseData,
      ApiErrorResponse,
      ResponseData,
      QKey | QueryKey
    >({
      ...baseOptions,
      ...customOptions,
      queryKey,
      queryFn: () => queryFn(...args),
    });

    return { ...query, setData, invalidate, remove };
  };
